import { Button, Dialog, DialogContent, DialogTitle, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { WSMPData } from "types/summary";

const useStyles = makeStyles(() => ({
  closeButton: {
    position: "absolute",
    right: 0,
    top: 6,
    color: "grey",
  },
  capitalize: {
    textTransform: "capitalize",
  },
  table: {
    width: 1500,
    overflow: "auto",
    textAlign: "center",
    border: "1px solid #ddd",
    borderTop: "none",
    borderLeft: "none",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  thead: {
    background: "#f7f7f7",
    borderBottom: "1px solid #ddd",
    position: "sticky",
    top: 0,
  },
  commonThead: {
    borderRight: "1px solid #ddd",
    padding: 5,
    textAlign: "center",
    verticalAlign: "middle",
    lineHeight: "20px",
  },
  tbody: {
    border: "1px solid #ddd",
    borderCollapse: "collapse",
  },
  row: {
    borderBottom: "1px solid #ddd",
    verticalAlign: "top",
  },
  commonTd: {
    borderRight: "1px solid #ddd",
    padding: 5,
    textAlign: "center",
    lineHeight: "20px",
  },
  firstCell: ({ width }: any) => ({
    width: width * 0.2,
    verticalAlign: "top",
  }),
  secondCell: ({ width }: any) => ({
    width: width * 0.2,
    verticalAlign: "top",
  }),
  seventhCell: ({ width }: any) => ({
    width: width * 0.2,
    maxWidth: width * 0.2,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  }),
  eighthCell: ({ width }: any) => ({
    width: width * 0.2,
  }),
  ninthCell: ({ width }: any) => ({
    width: width * 0.2,
  }),
}));

export interface WSMPDialogProps {
  open: boolean;
  onClose: () => void;
  data: WSMPData[];
}

const WSMPDialog: React.FC<WSMPDialogProps> = ({ open, onClose, data }) => {
  const jiraTable = useRef<HTMLTableElement>(null);
  const [width, setWidth] = useState(1430);
  const classes = useStyles({ width });

  useEffect(() => {
    // get tbody first row cell width from thead each cell width
    const tableWidth = jiraTable?.current?.clientWidth;
    setWidth(tableWidth as number);
  }, [data, open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth={true}
        maxWidth={"lg"}
        hideBackdrop
      >
        <DialogTitle>
          WSMP Status
          <Button
            className={classes.closeButton}
            onClick={onClose}
          >
            x
          </Button>
        </DialogTitle>
        <DialogContent
          dividers={true}
          style={{ width: 1280, overflow: "hidden" }}
        >
          <div
            style={{
              width: 1236,
              height: "40vh",
              overflow: "scroll",
              border: "1px solid #ddd",
            }}
          >
            <table
              ref={jiraTable}
              className={classes.table}
            >
              <thead
                id="thead"
                className={classes.thead}
              >
                <td
                  className={`${classes.firstCell} ${classes.commonThead}`}
                  style={{
                    verticalAlign: "middle",
                  }}
                >
                  Control Hub Organization ID
                </td>
                <td
                  className={`${classes.secondCell} ${classes.commonThead}`}
                  style={{
                    verticalAlign: "middle",
                  }}
                >
                  CI Org WSMP Status
                </td>
                <td className={`${classes.seventhCell} ${classes.commonThead}`}>Site Url</td>
                <td className={`${classes.eighthCell} ${classes.commonThead}`}>
                  WSMP Migration Date
                </td>
                <td className={`${classes.ninthCell} ${classes.commonThead}`}>WSMP Flag</td>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr
                    className={classes.row}
                    key={item.orgId}
                  >
                    <td className={`${classes.firstCell} ${classes.commonTd}`}>{item.orgId}</td>
                    <td className={`${classes.secondCell} ${classes.commonTd}`}>
                      {/* logic: 
                        1. wsmp flag all yes --> Done
                        2. have site & wsmpflag all no/null --> not start
                        3. no site --> N/A
                        4. other condition --> in progress*/}
                      {item.sites?.length
                        ? item.sites.every((i) => i.wsmpFlag === "Y")
                          ? "Done"
                          : item.sites.every((i) => !i.wsmpFlag || i.wsmpFlag === "N")
                            ? "Not Start"
                            : "In Progress"
                        : "N/A"}
                    </td>
                    <td colSpan={3}>
                      {item.sites.map((s) => (
                        <tr
                          key={s.siteKey}
                          style={{
                            height:
                              item.sites?.length === 1 &&
                              (!item.jiraContents?.length || item.jiraContents?.length === 1)
                                ? 50
                                : 30,
                          }}
                        >
                          <Tooltip title={s.siteUrl}>
                            <td
                              className={`${classes.seventhCell} ${classes.commonTd}`}
                              style={{
                                cursor: "pointer",
                                textAlign: "left",
                              }}
                            >
                              {s.siteUrl}
                            </td>
                          </Tooltip>
                          <td className={`${classes.eighthCell} ${classes.commonTd}`}>
                            {s.wsmpDate ? moment(s.wsmpDate).format("MM/DD/YYYY") : "N/A"}
                          </td>
                          <td
                            className={`${classes.ninthCell} ${classes.commonTd}`}
                            style={{
                              borderRight: "none",
                            }}
                          >
                            {s.wsmpFlag ? (s.wsmpFlag === "N" ? "No" : "Yes") : "N/A"}
                          </td>
                        </tr>
                      ))}
                      {item.jiraContents?.length > item.sites?.length
                        ? item.jiraContents
                            ?.slice(0, item.jiraContents?.length - item.sites?.length)
                            .map((_x, i) => (
                              <tr
                                key={i}
                                style={{
                                  height:
                                    (item.sites?.length === 1 || !item.sites?.length) &&
                                    (!item.jiraContents?.length || item.jiraContents?.length === 1)
                                      ? 50
                                      : 30,
                                }}
                              >
                                <td className={`${classes.seventhCell} ${classes.commonTd}`}></td>
                                <td className={`${classes.eighthCell} ${classes.commonTd}`}></td>
                                <td
                                  className={`${classes.ninthCell} ${classes.commonTd}`}
                                  style={{
                                    borderRight: "none",
                                  }}
                                ></td>
                              </tr>
                            ))
                        : null}
                      {!item.sites?.length && !item.jiraContents?.length ? (
                        <tr
                          style={{
                            height: 50,
                          }}
                        >
                          <td className={`${classes.seventhCell} ${classes.commonTd}`}></td>
                          <td className={`${classes.eighthCell} ${classes.commonTd}`}></td>
                          <td
                            className={`${classes.ninthCell} ${classes.commonTd}`}
                            style={{
                              borderRight: "none",
                            }}
                          ></td>
                        </tr>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WSMPDialog;
